import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ROLE_LABELS } from "@/constants/workflow";
import { Button } from "@/components/ui/button";

const ROLES = Object.keys(ROLE_LABELS);

/** Accounts: approval, deletion and account-level roles. No project data here. */
export function UsersSection({ users, onChanged }) {
  const call = async (fn, message) => {
    try {
      await fn();
      if (message) toast.success(message);
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const pending = users.filter((u) => u.approval_status === "pending");
  const others = users.filter((u) => u.approval_status !== "pending");

  const card = (user) => (
    <div
      key={user.id}
      data-testid={`admin-user-${user.email}`}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
          <p className="mono text-[11px] text-slate-500">{user.email}</p>
        </div>
        <span
          className={`mono ml-auto rounded px-2 py-0.5 text-[9px] uppercase tracking-wider ${
            user.approval_status === "approved"
              ? "bg-emerald-500/15 text-emerald-700"
              : "bg-amber-500/15 text-amber-700"
          }`}
        >
          {user.approval_status}
        </span>
        {user.approval_status !== "approved" ? (
          <Button
            data-testid={`approve-user-${user.email}`}
            onClick={() =>
              call(
                () => api.put(`/admin/users/${user.id}/approval`, { approval_status: "approved" }),
                `${user.email} approved`
              )
            }
            className="h-7 rounded-full bg-emerald-500/20 px-3 text-[10px] uppercase tracking-wider text-emerald-700 hover:bg-emerald-500/30"
          >
            Approve
          </Button>
        ) : null}
        <Button
          data-testid={`delete-user-${user.email}`}
          onClick={() => {
            if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
            call(() => api.delete(`/admin/users/${user.id}`), `${user.email} deleted`);
          }}
          className="h-7 rounded-full bg-red-500/15 px-3 text-[10px] uppercase tracking-wider text-red-700 hover:bg-red-500/25"
        >
          <Trash2 size={11} className="mr-1" /> Delete
        </Button>
      </div>

      <div className="mt-4">
        <p className="mono mb-2 text-[10px] uppercase tracking-wider text-slate-500">
          User roles
        </p>
        <div className="flex flex-wrap gap-1.5">
          {ROLES.map((role) => (
            <button
              key={role}
              data-testid={`role-${role}-${user.email}`}
              onClick={() =>
                call(() =>
                  api.put(`/admin/users/${user.id}/roles`, {
                    roles: user.roles.includes(role)
                      ? user.roles.filter((r) => r !== role)
                      : [...user.roles, role],
                  })
                )
              }
              className={`mono rounded-full px-3 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                user.roles.includes(role)
                  ? "bg-cyan-500/20 text-cyan-700"
                  : "border border-slate-200 text-slate-500 hover:bg-slate-100"
              }`}
            >
              {ROLE_LABELS[role]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[10.5px] text-slate-500">
          Project-specific roles are managed in the Access tab.
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="admin-users-section">
      {pending.length ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-3">
          <p
            data-testid="pending-users-notice"
            className="mono text-[10.5px] uppercase tracking-wider text-amber-800"
          >
            {pending.length} new registration{pending.length === 1 ? "" : "s"} waiting for approval
          </p>
        </div>
      ) : null}
      {pending.map(card)}
      {others.map(card)}
    </div>
  );
}
